import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    Renderer2,
    signal
} from '@angular/core';
import { app } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { DfDomModule } from '@df/dom/df-dom.module';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { BgColorOptions, RatioOptions } from '@df/ui/atomic/atomic-options';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollEvent, ScrollService } from '@df/util/service/scroll.service';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { BehaviorSubject, Subscription } from 'rxjs';
import { NCmsMinibasket } from '../../basket/minibasket/minibasket.component';
import { NPageStoreFinderPageType } from '../../catalog/page/store-finder/page-store-finder.component';
import { StickyFreeComponent } from '../../common/sticky-free/sticky-free.component';
import { iconsOptions } from '../../styles/cms.config';
import { NPageOffCanvas } from '../off-canvas/page-off-canvas.component';
import { PageOffCanvasSearchFormComponent } from '../off-canvas/search-form/page-off-canvas-search-form.component';
import { PageHeaderPromobarComponent } from './promobar/page-header-promobar.component';
import { PageHeaderSearchFormComponent } from './search-form/page-header-search-form.component';
import { PageHeaderMeganavMenuComponent } from './meganav/menu/page-header-meganav-menu.component';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { PageHeaderMeganavContentComponent } from './meganav/content/page-header-meganav-content.component';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { PageHeaderBasketQtyBulletDirective } from './basket-qty-bullet/page-header-basket-qty-bullet.directive';
import { DfEventService } from '@df/core/service/df-event';
import { AbService } from '@df/module-ab/ab.service';
import { BASKET_INTERACTION_EXPERIMENT_ID } from '../../basket/notification-bar/basket-notification-bar.component';

export const pageHeaderStickyOffForce = new BehaviorSubject<boolean>(false);

export namespace NPageHeader {
    export const BLOCK_ID = 'page-header';

    export interface IBlockDataContent {
        logo: string;
        meganav: IMeganavItem[];

        additionalImage?: string;
        additionalUrl?: string;
        additionalTitle?: string;
        additionalDescription?: string;

        promobarEnabled: boolean;
        promobarBackground?: string;
        promobarColor?: string;
        promobarMessageDuration?: number;
        promobarMessages?: IPromobarMessage[];
        promobarCountTo?: number;
        promobarDisableAfterCountdown?: boolean;
    }

    export interface IPromobarMessage {
        isAdvanced: boolean;
        copy?: string;
        link?: string;
        blocks?: IPromobarContentBlock[];
    }
    /**
     * Meganav menu entry
     */
    export interface IMeganavItem extends IMenuFields, NICmsContentComponent.IData {
        columns: { submenus: IMeganavItemSubmenu[] }[];
    }

    export enum ESubmenuLayout {
        links,
        linksWithDescription,
        image,
        promoBanner
    }

    /**
     * Submenu column
     */
    export interface IMeganavItemSubmenu {
        layout: ESubmenuLayout;
        title?: string;
        /**
         * Image description
         */
        description?: string;
        image: string;
        /**
         * Image ratio
         */
        ratio: string;
        ctaCopy?: string;
        link: string;
        linksList: ISubmenuLinkEntry[];
        linksWithDescriptionList: ISubmenuLinkEntry[];
    }

    /**
     * Submenu link entry
     */
    export interface ISubmenuLinkEntry extends IMenuFields {
        image?: string;
        description?: string;
        icon?: string;
    }

    interface IMenuFields {
        /**
         * Meganav item text
         */
        text: string;
        /**
         * Meganav item link
         */
        link?: string;
    }

    export const STD_MENU_FIELDS = [
        {
            key: 'text',
            type: NICmsContentField.EFieldType.text,
            title: 'Text',
            default: 'Some link',
            required: true
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link'
        }
    ];

    export const meganavItemFields = [
        ...NPageHeader.STD_MENU_FIELDS,
        <NICmsContentField.IRepeaterField>{
            key: 'columns',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Menu columns',
            itemName: 'Column',
            description: 'If no columns are added, the menu item will be a link',
            maxSize: 5,
            fields: [
                <NICmsContentField.IRepeaterField>{
                    key: 'submenus',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Submenu',
                    itemName: 'Element',
                    itemNameField: 'title',
                    complex: true,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'layout',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Layout',
                            required: true,
                            options: [
                                { key: NPageHeader.ESubmenuLayout.links, title: 'List of links' },
                                { key: NPageHeader.ESubmenuLayout.linksWithDescription, title: 'List of links with description' },
                                { key: NPageHeader.ESubmenuLayout.image, title: 'Image' },
                                { key: NPageHeader.ESubmenuLayout.promoBanner, title: 'Promo banner' }
                            ],
                            default: NPageHeader.ESubmenuLayout.links
                        },
                        {
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image',
                            required: true,
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) =>
                                data?.layout === NPageHeader.ESubmenuLayout.image || data?.layout === NPageHeader.ESubmenuLayout.promoBanner
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'ratio',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Image ratio',
                            options: RatioOptions,
                            description: 'Proportions width x height',
                            default: 'ratio-1-1',
                            required: true,
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) =>
                                data?.layout === NPageHeader.ESubmenuLayout.image || data?.layout === NPageHeader.ESubmenuLayout.promoBanner
                        },
                        {
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Title'
                        },
                        <NICmsContentField.ITextField>{
                            type: NICmsContentField.EFieldType.textarea,
                            key: 'description',
                            title: 'Description',
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) => data?.layout === NPageHeader.ESubmenuLayout.promoBanner
                        },
                        {
                            key: 'ctaCopy',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Button copy',
                            description: 'The button will not appear if not set',
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) => data?.layout !== NPageHeader.ESubmenuLayout.image
                        },
                        {
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link'
                        },
                        <NICmsContentField.IRepeaterField>{
                            key: 'linksList',
                            type: NICmsContentField.EFieldType.repeater,
                            title: 'List of links',
                            itemName: 'Link',
                            itemNameField: 'text',
                            required: true,
                            fields: [...NPageHeader.STD_MENU_FIELDS],
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) => data?.layout === NPageHeader.ESubmenuLayout.links
                        },
                        <NICmsContentField.IRepeaterField>{
                            type: NICmsContentField.EFieldType.repeater,
                            key: 'linksWithDescriptionList',
                            title: 'List of links with description',
                            itemName: 'Link',
                            required: true,
                            itemNameField: 'text',
                            canShow: (data: NPageHeader.IMeganavItemSubmenu) =>
                                data?.layout === NPageHeader.ESubmenuLayout.linksWithDescription,
                            fields: [
                                ...NPageHeader.STD_MENU_FIELDS,
                                <NICmsContentField.ITextField>{
                                    type: NICmsContentField.EFieldType.textarea,
                                    key: 'description',
                                    title: 'Description',
                                    required: true
                                },
                                <NICmsContentField.IChoiceField>{
                                    type: NICmsContentField.EFieldType.choice,
                                    key: 'icon',
                                    title: 'Icon',
                                    options: iconsOptions,
                                    previewMode: 'icon'
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ];

    export interface IPromobarContentBlock {
        type: TPromobarSlideBlockType;
        copy: string;
        link: string;
    }

    export type TPromobarSlideBlockType = 'text' | 'button' | 'countdown';

    export const MEGANAV_GROUP_ID = 'meganav';

    export const MAGANV_CONTENT_ITEM_COMPONENT_ID = 'page-header-meganav-content-item';
}

@Component({
    standalone: true,
    imports: [
        CommonModule,
        PageHeaderPromobarComponent,
        DfDomModule,
        StickyFreeComponent,
        DfModuleImageModule,
        DfUiModule,
        ICmsClientModule,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        PageHeaderSearchFormComponent,
        PageHeaderMeganavMenuComponent,
        DfUiModule,
        RouterModule,
        HrefComponent,
        PageHeaderMeganavContentComponent,
        DfDomModule,
        PageHeaderBasketQtyBulletDirective
    ],
    selector: 'page-header',
    templateUrl: 'page-header.html',
    styleUrl: 'page-header.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageHeaderComponent>({
    id: NPageHeader.BLOCK_ID,
    name: 'Page Header',
    description: 'Page header block',
    onDemandOnly: true,
    block: {
        url: NPageHeader.BLOCK_ID,
        scope: 'website',
        autoload: 'bootstrap'
    },
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionGeneral',
            title: 'General'
        },
        {
            key: 'logo',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo Image',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'meganav',
            title: 'Menu Items',
            itemName: 'Item',
            itemNameField: 'text',
            complex: true,
            fields: NPageHeader.meganavItemFields
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectioTalkToExpert',
            title: 'Talk to an expert section'
        },
        <NICmsContentField.IField>{
            key: 'additionalImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        <NICmsContentField.IField>{
            key: 'additionalUrl',
            type: NICmsContentField.EFieldType.text,
            title: 'Url',
            description: 'Link will be applied to the whole element'
        },
        <NICmsContentField.ITextField>{
            key: 'additionalTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.ITextField>{
            key: 'additionalDescription',
            type: NICmsContentField.EFieldType.text,
            title: 'Description'
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionpromobar',
            title: 'Promo banner'
        },
        {
            key: 'promobarEnabled',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Enabled',
            required: false
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobarBackground',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background colour',
            required: false,
            options: BgColorOptions,
            previewMode: 'color',
            default: 'bg-col-6',
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobarEnabled
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobarColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text colour',
            required: false,
            options: () => app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors,
            previewMode: 'color',
            default: 'col-w',
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobarEnabled
        },

        <NICmsContentField.INumberField>{
            key: 'promobarMessageDuration',
            type: NICmsContentField.EFieldType.number,
            title: 'Message duration',
            description: 'Time in seconds for each message to be displayed',
            min: 1,
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobarEnabled
        },
        <NICmsContentField.IRepeaterField>{
            key: 'promobarMessages',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Messages',
            itemName: 'Message',
            complex: true,
            maxSize: 3,
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobarEnabled,
            fields: [
                {
                    key: 'isAdvanced',
                    description: 'Enable to add more complex elements like countdown timer and button',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Complex structure',
                    required: false,
                    default: false
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                },
                {
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    canShow: (data: NPageHeader.IPromobarMessage) => !data?.isAdvanced
                },
                <NICmsContentField.IRepeaterField<NPageHeader.IPromobarContentBlock>>{
                    key: 'blocks',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Elements',
                    canShow: (data: NPageHeader.IPromobarMessage) => data.isAdvanced,
                    itemName: 'element',
                    itemNameField: 'type',
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Type',
                            required: true,
                            default: 'text',
                            options: [
                                { key: 'text', title: 'Text' },
                                { key: 'button', title: 'Button' },
                                { key: 'countdown', title: 'Countdown' }
                            ]
                        },
                        {
                            key: 'copy',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Copy',
                            required: true,
                            canShow: (data: NPageHeader.IPromobarContentBlock) => ['text', 'button'].includes(data?.type)
                        },
                        {
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link',
                            required: true,
                            canShow: (data: NPageHeader.IPromobarContentBlock) => ['button'].includes(data?.type)
                        }
                    ]
                }
            ]
        },

        {
            key: 'promobarCountTo',
            type: NICmsContentField.EFieldType.text,
            title: 'promobar countdown to yyyy-mm-dd HH:mm',
            required: true,
            canShow: (data: NPageHeader.IBlockDataContent) =>
                data?.promobarEnabled &&
                !!data?.promobarMessages?.find(message => message.isAdvanced && message.blocks?.find(block => block.type === 'countdown'))
        },
        {
            key: 'promobarDisableAfterCountdown',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide promobar when countdown finishes',
            required: false,
            canShow: (data: NPageHeader.IBlockDataContent) =>
                data?.promobarEnabled &&
                !!data?.promobarMessages?.find(message => message.isAdvanced && message.blocks?.find(block => block.type === 'countdown'))
        }
    ]
})
export class PageHeaderComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageHeader.IBlockDataContent;

    @Input()
    offCanvasState!: BehaviorSubject<NPageOffCanvas.EOpenState>;

    /**
     * Minibasket modal id
     */
    readonly minibasketModalId = NCmsMinibasket.BLOCK_ID;

    nodes$ = new BehaviorSubject<HTMLElement[]>([]);

    /**
     * If content is set
     */
    hasContent$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    protected isPlatformBrowser = inject(PLATFORM_ID) === 'browser';

    /**
     * If the header content has been taken out of the document flow.
     *
     * Detaching is what lets the XL meganav content overlay the page rather
     * than push it down, so it is only needed from L up. It costs two layout
     * shifts to do it: the host collapses to zero until the JS measured height
     * lands on it, and the element height directive republishes the header
     * height as zero while the content is out of flow, which drags the off
     * canvas with it. S and M therefore keep the header in flow.
     */
    protected readonly isDetached = signal(false);

    readonly isPromobarHidden = signal(false);
    private readonly router = inject(Router);
    private readonly toggleService = inject(ToggleService);
    private readonly scrollService = inject(ScrollService);
    private readonly meta = inject(Meta);
    private readonly afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private readonly elementHeightService = inject(ElementHeightService);
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly dfEventService = inject(DfEventService);
    private readonly abService = inject(AbService);
    private height = 0;

    /**
     * Header height as measured while the promobar is visible.
     *
     * The scroll threshold must not read `height`, because hiding the promobar
     * is what shrinks `height`, so the decision would be feeding on its own
     * result. On S and M the header stays in flow, so that collapse also moves
     * page content and emits more scroll events, and the page ends up
     * oscillating. Comparing against the expanded value keeps the threshold
     * still while the header animates.
     */
    private expandedHeight = 0;

    /**
     * Scroll position at which the promobar last changed visibility.
     */
    private lastToggleScrollTop?: number;

    /**
     * Scroll distance required between two promobar visibility changes.
     * Without a floor, a 1px scroll delta is enough to flip the bar back.
     */
    private readonly promobarToggleThreshold = 24;

    /**
     * Height reserved for the header in the page flow.
     *
     * Hiding the promobar on scroll collapses the header, and while the host
     * followed the live height the whole page moved up with it, then back down
     * when the bar returned — animated, on every change of scroll direction.
     * Scrolling is not user input as far as CLS is concerned, so every frame of
     * that counted. While the header is collapsed, and until it has finished
     * animating back open, the host holds the expanded height, so the header
     * shrinks inside its space instead of pulling the page with it. The rest
     * of the time the host follows the live height: keeping the tallest height
     * ever seen also kept heights the header only passes through while it
     * loads, leaving a blank band under it.
     */
    private reservedHeight = 0;

    /**
     * Releases the reservation if the header never grows back to it, i.e. it
     * really did get shorter while collapsed. Longer than the 0.3s transition.
     */
    private reservedHeightReleaseTimer?: ReturnType<typeof setTimeout>;

    headerHeightSub?: Subscription;

    /**
     * Hook to override when sticky should be off
     */
    get isStickyOff() {
        if (pageHeaderStickyOffForce.value) {
            return true;
        }
        if (this.isCheckoutProcess) {
            return true;
        }
        const currentModel = this.meta.model;
        if (currentModel?.type === 'product' && this.client.sizeId > 1) {
            return true;
        }
        return (
            currentModel?.type === 'page' &&
            this.client.sizeId < 2 &&
            (currentModel as ICmsPage).data?.pageType === NPageStoreFinderPageType.typeId
        );
    }

    get isCheckoutProcess(): boolean {
        const url = this.router.url;
        return !!(url.match(/^\/checkout/) && !url.match(/^\/checkout\/success/));
    }

    /**
     * Init override to set up subscriptions
     */
    override ngOnInit(): void {
        super.ngOnInit();
        // trigger change detection after content is initiated
        this.subscriptions.add(
            this.afterContentInitDirectiveService.loaded.subscribe(value => {
                if (value) {
                    this.detectChanges();
                }
            })
        );
        this.subscriptions.add(
            this.scrollService.scroll$.subscribe((event: ScrollEvent) => {
                const isHiddenBeforeScroll = this.isPromobarHidden();
                const reference = this.expandedHeight || this.height;
                let isHidden: boolean;
                /**
                 * For category and search pages, the promobar is visible only if the user is at the top of the page
                 */
                if (this.meta.model?.type === 'category' || this.meta.model?.type === 'search') {
                    isHidden = event.scrollTop - reference <= 0 ? false : true;
                } else {
                    isHidden = event.isScrollingDown && event.scrollTop - reference >= 0;
                }
                if (isHiddenBeforeScroll !== isHidden) {
                    // ignore flips that follow too closely on the last one, they are the feedback loop, not the user
                    if (
                        this.lastToggleScrollTop !== undefined &&
                        Math.abs(event.scrollTop - this.lastToggleScrollTop) < this.promobarToggleThreshold
                    ) {
                        return;
                    }
                    this.lastToggleScrollTop = event.scrollTop;
                    this.isPromobarHidden.set(isHidden);
                    this.updateHostHeight();
                    this.detectChanges();
                }
            })
        );

        this.subscriptions.add(
            this.offCanvasState.subscribe(() => {
                this.updateHostHeight();
                this.detectChanges();
            })
        );
        this.nodes$.next([this.elementRef.nativeElement]);
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.detectChanges();
                }
            })
        );
        this.subscriptions.add(pageHeaderStickyOffForce.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.abService.getSubject(BASKET_INTERACTION_EXPERIMENT_ID).subscribe(subject => {
                this.detectChanges();
            })
        );
    }

    ngAfterViewInit(): void {
        this.watchHeaderHeight();
    }

    protected override onBreakpoint(): void {
        this.watchHeaderHeight();
    }

    toggleOffCanvas(): void {
        this.isOffCanvasOpen
            ? this.offCanvasState.next(NPageOffCanvas.EOpenState.closed)
            : this.offCanvasState.next(NPageOffCanvas.EOpenState.openMenu);
    }

    openOffCanvasSearch(): void {
        this.offCanvasState.next(NPageOffCanvas.EOpenState.openSearch);
        this.toggleService.open(PageOffCanvasSearchFormComponent.toggleId);
    }

    get isOffCanvasOpen(): boolean {
        return this.offCanvasState.getValue() === NPageOffCanvas.EOpenState.openMenu;
    }

    override detectChanges(): void {
        super.detectChanges();

        const hasContent = !!this.content;

        if (this.hasContent$.value !== hasContent) {
            this.hasContent$.next(!!hasContent);
        }
    }

    protected watchHeaderHeight(): void {
        if (this.headerHeightSub) {
            this.headerHeightSub.unsubscribe();
        }
        // a breakpoint change, or the promobar itself appearing or being
        // dismissed, is a genuine change of size, not a collapse
        this.reservedHeight = 0;
        this.headerHeightSub = this.elementHeightService.getState('page-header').subscribe(value => {
            if (value) {
                this.height = value;
                if (!this.isCollapsed && !this.reservedHeight) {
                    this.expandedHeight = value;
                }
                this.updateHostHeight();
                this.updateDetached();
            }
        });
    }

    /**
     * Whether scrolling or the open menu has collapsed part of the header.
     */
    private get isCollapsed(): boolean {
        return this.isPromobarHidden() || this.isOffCanvasOpen;
    }

    /**
     * Sizes the host: the expanded height while the header is collapsed or
     * animating back open, the live height otherwise.
     */
    private updateHostHeight(): void {
        clearTimeout(this.reservedHeightReleaseTimer);

        if (this.isCollapsed) {
            this.reservedHeight = this.reservedHeight || this.expandedHeight;
        } else if (this.reservedHeight && this.height < this.reservedHeight) {
            this.reservedHeightReleaseTimer = setTimeout(() => {
                this.reservedHeight = 0;
                this.updateHostHeight();
            }, 500);
        } else {
            this.reservedHeight = 0;
        }

        const height = Math.max(this.height, this.reservedHeight);
        if (height) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${height}px`);
        }
    }

    /**
     * Detaches the header content once its space is reserved on the host,
     * never before, otherwise the whole page shifts up into the gap.
     */
    private updateDetached(): void {
        const shouldDetach = this.isPlatformBrowser && !!this.height && !this.client.isS && !this.client.isM;

        if (this.isDetached() !== shouldDetach) {
            this.isDetached.set(shouldDetach);
            this.detectChanges();
        }
    }

    toggleCmsNode(element: HTMLElement, state: boolean) {
        const nodes = this.nodes$.value;
        if (state) {
            if (!nodes.includes(element)) {
                this.nodes$.next([...nodes, element]);
            }
        } else {
            if (nodes.includes(element)) {
                this.nodes$.next(nodes.filter(node => node !== element));
            }
        }
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    protected get isBasketInteractionExperiment(): boolean {
        return this.abService.getVariant(BASKET_INTERACTION_EXPERIMENT_ID) === 'B';
    }

    protected trackBasketClick() {
        this.dfEventService.dispatch({
            id: 'dfGtmCustomEvent',
            data: {
                name: 'basket_icon_click',
                data: undefined
            }
        });
    }
}
